import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

type AccordionContextValues = {
  open: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: boolean;
};

export const AccordionContext = createContext({} as AccordionContextValues);
export const useAccordionContext = () => useContext(AccordionContext);

type AccordionProps = {
  children: ReactNode;
  isOpen?: boolean;
};

/**
 * The main provider of the Accordion that passes down all of the states and functions.
 *
 * @param children - What is displayed in the main provider of the Accordion
 * @param isOpen - The default value of whether the Accordion is open or not
 * @returns React.FC
 */
export const Accordion = ({ children, isOpen: defaultIsOpen = false }: AccordionProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(defaultIsOpen);

  useEffect(() => {
    setIsOpen(defaultIsOpen);
  }, [defaultIsOpen]);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((prev) => !prev);

  return <AccordionContext.Provider value={{ open, close, toggle, isOpen }}>{children}</AccordionContext.Provider>;
};

/**
 * The children of this header will not be hidden if the Accordion is toggled.
 *
 * @param children - What is displayed in the header of the Accordion
 * @returns React.FC
 */
type AccordionHeaderProps = {
  children: ReactNode;
};
export const AccordionHeader = ({ children }: AccordionHeaderProps) => {
  return <>{children}</>;
};

/**
 * The children of this header will be hidden if the Accordion is toggled.
 *
 * @param children - What is displayed in the main body of the Accordion
 * @returns React.FC
 */
type AccordionBodyProps = {
  children: ReactNode;
};
export const AccordionBody = ({ children }: AccordionBodyProps) => {
  const { isOpen } = useAccordionContext();
  if (!isOpen) return null;

  return <>{children}</>;
};
